import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints, formatApiError, fmtUSD, fmtTime, API } from "@/lib/api";
import { ApiKeysSection, WebhookSection } from "@/components/HandoffSettings";
import StateBadge from "@/components/StateBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowRightLeft, Play, Zap, FastForward } from "lucide-react";
import { useBrand } from "@/context/BrandContext";

const inputCls = "bg-white border-zinc-200 rounded-sm h-9 font-mono text-[12px] focus-visible:ring-orange-500/40";
const SAMPLE = {
  external_deal_id: "MDO-" + Math.floor(1000 + Math.random() * 9000),
  motion: "edp_burn_down",
  buyer_company: "Acme Robotics Inc.",
  buyer_aws_account_id: "482910573361",
  product: "Enterprise Platform",
  tcv_usd: 750000,
  term_months: 36,
  preferred_reseller: "Presidio",
  opportunity_id: "SF-OPP-8842",
  incentive: "EDP commit burn-down — $1.2M remaining",
  notes: "",
  bypass: false,
  auto_dispatch: true,
};

function SimulatePush({ onDone }) {
  const brand = useBrand();
  const [form, setForm] = useState(SAMPLE);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const push = async () => {
    setBusy(true);
    try {
      const r = await endpoints.pushHandoffDeal({ ...form, tcv_usd: Number(form.tcv_usd), term_months: Number(form.term_months) });
      toast.success(r.idempotent ? "Deal already known (idempotent)" : "Deal received — agent dispatched", { description: r.run_id || r.id });
      setForm({ ...form, external_deal_id: "MDO-" + Math.floor(1000 + Math.random() * 9000) });
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border border-zinc-200 p-6 bg-zinc-50" data-testid="simulate-push">
      <div className="flex items-center gap-2 mb-1">
        <Zap className="w-4 h-4 text-orange-600" />
        <span className="tx-overline">Simulate a {brand.source_app_name} hand-off</span>
      </div>
      <p className="text-[12px] text-zinc-500 mb-4">Same payload {brand.source_app_name} sends; uses your session instead of an API key.</p>
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="deal-external-id-input" value={form.external_deal_id} onChange={set("external_deal_id")} className={inputCls} placeholder="external_deal_id" />
        <select data-testid="deal-motion-select" value={form.motion} onChange={set("motion")} className="h-9 bg-white border border-zinc-200 rounded-sm px-2 text-[12px] font-mono text-zinc-800">
          {["new_deal", "renewal", "edp_burn_down", "expansion"].map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <Input data-testid="deal-buyer-input" value={form.buyer_company} onChange={set("buyer_company")} className={inputCls} placeholder="buyer_company" />
        <Input data-testid="deal-aws-input" value={form.buyer_aws_account_id} onChange={set("buyer_aws_account_id")} className={inputCls} placeholder="12-digit AWS account" />
        <Input data-testid="deal-product-input" value={form.product} onChange={set("product")} className={`${inputCls} col-span-2`} placeholder="product" />
        <Input data-testid="deal-tcv-input" type="number" value={form.tcv_usd} onChange={set("tcv_usd")} className={inputCls} placeholder="tcv_usd" />
        <Input data-testid="deal-term-input" type="number" value={form.term_months} onChange={set("term_months")} className={inputCls} placeholder="term_months" />
        <Input data-testid="deal-reseller-input" value={form.preferred_reseller || ""} onChange={set("preferred_reseller")} className={inputCls} placeholder="preferred_reseller" />
        <Input data-testid="deal-opp-input" value={form.opportunity_id || ""} onChange={set("opportunity_id")} className={inputCls} placeholder="opportunity_id" />
        <Input data-testid="deal-incentive-input" value={form.incentive || ""} onChange={set("incentive")} disabled={form.bypass} className={`${inputCls} col-span-2`} placeholder="incentive" />
        <label className="col-span-2 flex items-center gap-2 text-[12px] text-zinc-700 border border-zinc-200 bg-white px-3 h-9">
          <input type="checkbox" data-testid="deal-bypass-checkbox" checked={form.bypass} onChange={(e) => setForm({ ...form, bypass: e.target.checked, incentive: e.target.checked ? "" : form.incentive })} className="accent-orange-600" />
          <FastForward className="w-3.5 h-3.5 text-orange-600" /> Straight to private offer (buyer bypassed benefit options)
        </label>
      </div>
      <Button onClick={push} disabled={busy} data-testid="push-deal-button" className="w-full mt-3 h-9 bg-orange-600 hover:bg-orange-500 text-white rounded-sm gap-2">
        <Play className="w-3.5 h-3.5" /> {busy ? "Pushing…" : "Push deal → dispatch agent"}
      </Button>
    </div>
  );
}

export default function Handoff() {
  const brand = useBrand();
  const [deals, setDeals] = useState([]);
  const load = () => endpoints.handoffDeals().then((r) => setDeals(r.deals));
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const dispatch = async (id) => {
    try {
      const r = await endpoints.dispatchDeal(id);
      toast.success("Agent dispatched", { description: r.run_id });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-[1400px] px-12 py-14" data-testid="page-handoff">
      <div className="tx-overline mb-2">{brand.source_app_name} → {brand.app_name}</div>
      <h1 className="font-heading font-black text-4xl mb-2">{brand.handoff_label}</h1>
      <p className="text-zinc-600 text-[14px] max-w-2xl mb-8">
        Deals from {brand.source_app_name} (buyer side of {brand.seller_motion_name}) land here and become
        agent runs immediately. Status flows back through the signed webhook.
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_440px] gap-10">
        <div>
          <div className="tx-overline mb-3">Inbound deals · {deals.length}</div>
          <div className="border border-zinc-200 divide-y divide-zinc-200" data-testid="deals-list">
            {deals.length === 0 && <div className="px-5 py-8 text-[12px] text-zinc-500">No deals handed off yet. Generate an API key below and point {brand.source_app_name} at <span className="font-mono text-zinc-700">POST {API}/handoff/deals</span>.</div>}
            {deals.map((d) => (
              <div key={d.id} data-testid="deal-row" className="px-5 py-4 grid grid-cols-[minmax(0,1fr)_auto] gap-4">
                <Link to={`/handoff/${d.id}`} data-testid="deal-open-timeline" className="min-w-0 block group">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[10px] text-orange-600">{d.external_deal_id}</span>
                    <span className="font-mono text-[10px] text-zinc-400">{brand.source_app_name} · {d.motion} · via {d.received_via}</span>
                    {d.bypass && <span data-testid="deal-bypass-badge" className="font-mono text-[9px] text-orange-700 border border-orange-300 bg-orange-50 px-1.5 py-0.5 flex items-center gap-1"><FastForward className="w-3 h-3" /> Straight to private offer</span>}
                  </div>
                  <div className="text-[13px] text-zinc-900 group-hover:text-orange-700 transition-colors">{d.buyer_company} <span className="text-zinc-500">·</span> {d.product}</div>
                  <div className="font-mono text-[11px] text-zinc-500 mt-0.5">{fmtUSD(d.tcv_usd)} · {d.term_months}mo · {d.preferred_reseller || "no reseller pref"} · {fmtTime(d.created_at)}</div>
                  {d.incentive && <div className="text-[11px] text-amber-700 mt-1">incentive: {d.incentive}</div>}
                  <div className="font-mono text-[10px] text-zinc-400 mt-1.5 group-hover:text-zinc-600">view timeline →</div>
                </Link>
                <div className="flex flex-col items-end gap-2">
                  <StateBadge state={d.status === "received" ? "draft" : d.status === "dispatched" ? "running" : d.status} />
                  {d.run_id ? (
                    <Link to={`/agent?run=${d.run_id}`} data-testid="deal-open-run" className="font-mono text-[10px] text-zinc-600 hover:text-orange-600 flex items-center gap-1">
                      <ArrowRightLeft className="w-3 h-3" /> {d.run_id}
                    </Link>
                  ) : (
                    <button onClick={() => dispatch(d.id)} data-testid="deal-dispatch-button" className="h-7 px-2.5 border border-zinc-300 hover:border-orange-500/60 hover:text-orange-600 text-[11px] font-mono">Dispatch</button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 border border-zinc-200 p-5 bg-white">
            <div className="tx-overline mb-2">Integration contract</div>
            <pre className="font-mono text-[11px] text-zinc-600 overflow-x-auto leading-relaxed">{`curl -X POST ${API}/handoff/deals \\
  -H "X-API-Key: cee_live_…" -H "Content-Type: application/json" \\
  -d '{"external_deal_id":"MDO-1001","motion":"edp_burn_down",
       "buyer_company":"Acme Robotics Inc.","buyer_aws_account_id":"482910573361",
       "product":"Enterprise Platform","tcv_usd":750000,"term_months":36,
       "preferred_reseller":"Presidio","opportunity_id":"SF-OPP-8842",
       "incentive":"EDP burn-down","bypass":false,"auto_dispatch":true}'
   // bypass:true = buyer skipped benefit options → straight private offer, no playbook

→ 201 {"id":"deal-…","run_id":"run-…","status":"dispatched","status_url":"/api/handoff/deals/deal-…"}
   GET status_url (same key) → deal + run {status: awaiting_approval | completed | failed | cancelled}
   Webhook → POST {url}  X-Transactr-Event: run.awaiting_approval  X-Transactr-Signature: t=…,v1=…`}</pre>
          </div>
        </div>

        <div className="space-y-6">
          <SimulatePush onDone={load} />
          <ApiKeysSection />
          <WebhookSection />
        </div>
      </div>
    </div>
  );
}
